/* Pricing — a faithful React port of the prototype's rPricing screen.
 *
 * One price slider per live generation of every active product. Each row shows
 * the live margin (price − blended unit cost) and the "price points" that price
 * earns toward the Overall Rating against the segment's reference price and price
 * concern. A ladder check warns when an older generation is priced at or above a
 * newer one (buyers ignore it); a per-card footer states the segment reference,
 * concern weights and the resulting positioning.
 *
 * Prices are staged in the decisions draft (store.setProductGen → d.products[].
 * gens[].price) and previewed live through buildWorkingGame, which replays the
 * same intent the server's applyDecisions runs at advance — so the margin and
 * price points on screen are exactly what resolves. Sub-sections are plain render
 * functions (not nested components) so React reconciles in place and the page
 * never scroll-jumps. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { Engine, SEGMENTS, fmt$, fmtK, clamp } from '@venturewrist/engine';
import { useDecisions } from '../../store/decisions';
import { buildWorkingGame } from '../../lib/designState';
import { EmptyNote } from '../../components/ui';
import type { ScreenProps } from './types';
import '../../styles/pricing.css';

const Eng = Engine as any;
const SEGS = SEGMENTS as any[];

const WhyChip = () => <span className="tooltip-why" title="Learn the theory">?</span>;

/** status chip — mirrors the prototype's genChip(): the one fixed status vocabulary. */
function GenChip({ st, label }: { st: string; label: string }) {
  const S = (Eng.STATUS as any)[st];
  if (!S) return null;
  return <span className="gen-chip" style={{ color: S.color }}>{S.icon} {label}</span>;
}

/** seg-tag — mirrors the prototype's segTag(). */
function SegTag({ segId }: { segId: string }) {
  const s = SEGS.find((x) => x.id === segId);
  if (!s) return null;
  return <span className="seg-tag" style={{ background: s.color + '22', color: s.color }}>{s.icon} {s.name}</span>;
}

/** concernTag — mirrors the prototype's concernTag(): P{pc}·Q{qc}·B{bc}. */
const concernTag = (seg: any) => `P${seg.concerns.pc}·Q${seg.concerns.qc}·B${seg.concerns.bc}`;

export function Pricing({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const setProductGen = useDecisions((z) => z.setProductGen);

  // Live working game: server state + staged price edits, replayed through the
  // engine's own mutators so margin / price points match what the server resolves.
  const g = useMemo(() => buildWorkingGame(detail.state, draft), [detail.state, draft]);

  const models = Eng.activeModels(g) as any[];

  if (models.length === 0) {
    return (
      <div className="pr-root">
        <EmptyNote>No products to price yet. Design one on the Design screen first.</EmptyNote>
      </div>
    );
  }

  // ── per-generation price row ───────────────────────────────────────────────
  const genRow = (m: any, gv: any, seg: any, multi: boolean) => {
    const st = Eng.genStatus(m, gv) as string;
    const uc = Eng.unitCost(gv.spec, g, gv.production);
    const margin = gv.price - uc;
    // rating points added at country income 1.0: (ref − price) ÷ ref × price concern
    const pricePts = clamp((seg.refPrice - gv.price) / seg.refPrice, -1.5, 1) * seg.concerns.pc;
    return (
      <div className={'gen-row' + (st === 'sellthrough' ? ' dim' : '')} key={gv.gen} data-tut="pricing-price">
        {multi ? <GenChip st={st} label={'Gen ' + gv.gen} /> : null}
        <input
          type="range" min={49} max={899} step={10} value={gv.price}
          onChange={(e) => setProductGen(m.id, gv.gen, { price: +e.target.value })}
          aria-label={(multi ? 'Gen ' + gv.gen + ' ' : '') + 'price'}
        />
        <b style={{ width: 58, textAlign: 'right', fontSize: 15 }}>{fmt$(gv.price)}</b>
        <span
          className="sub" style={{ textAlign: 'right', minWidth: 170 }}
          title={`Price points added to your Overall Rating at country income 1.0: (ref − price) ÷ ref × price concern ${seg.concerns.pc}%`}
        >
          margin <b className={margin > 0 ? 'pos' : 'neg'}>{fmt$(margin)}</b> · price pts{' '}
          <b className={pricePts >= 0 ? 'pos' : 'neg'}>{(pricePts >= 0 ? '+' : '') + pricePts.toFixed(0)}</b>
          {st === 'sellthrough' ? <><br />clearing {fmtK(gv.inventory)} inventory</> : null}
        </span>
      </div>
    );
  };

  // ── ladder check (§6.2): an older gen priced ≥ a newer gen is ignored ───────
  const ladderWarning = (live: any[]) => {
    const asc = live.slice().sort((a, b) => a.gen - b.gen);
    for (let i = 0; i < asc.length - 1; i++)
      for (let j = i + 1; j < asc.length; j++)
        if (asc[i].price >= asc[j].price)
          return (
            <div className="sub" style={{ color: 'var(--warn)', marginTop: 8 }}>
              ⚠ Gen {asc[i].gen} costs {asc[i].price === asc[j].price ? 'as much as' : 'more than'} Gen {asc[j].gen} — buyers
              will ignore it. Ladder older generations below newer ones.
            </div>
          );
    return null;
  };

  // ── per-product card ────────────────────────────────────────────────────────
  const productCard = (m: any) => {
    const seg = SEGS.find((s) => s.id === m.targetSeg);
    const live = (Eng.liveGens(m) as any[]).slice().sort((a, b) => b.gen - a.gen);
    const multi = live.length > 1 || m.gens.length > 1;
    const cur = Eng.currentGen(m);
    const positioning =
      cur.price < seg.refPrice * 0.85 ? 'Penetration — share over margin'
      : cur.price > seg.refPrice * 1.2 ? 'Skimming — needs brand & quality'
      : 'Near reference — balanced';
    return (
      <div className="card" key={m.id}>
        <div className="spread" style={{ marginBottom: 6 }}>
          <b>{m.name}</b>
          <SegTag segId={m.targetSeg} />
        </div>
        {live.map((gv) => genRow(m, gv, seg, multi))}
        {ladderWarning(live)}
        <div className="grid2" style={{ gap: 8, marginTop: 10 }}>
          <div data-tut="pricing-ref">
            <div className="sub">Ref price · concerns</div>
            <b>{fmt$(seg.refPrice)}</b>{' '}
            <span className="sub" title="Price / Quality / Brand concern weights">{concernTag(seg)}</span>
          </div>
          <div>
            <div className="sub">Positioning</div>
            <b style={{ fontWeight: 600, fontSize: 12 }}>{positioning}</b>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pr-root">
      <div className="screen-head">
        <h2>🏷️ Pricing<WhyChip /></h2>
        <div className="sub">
          Price against each segment's reference price and <b>price concern</b> — the share of 100 concern points the
          segment puts on price. Below reference earns positive price points toward your Overall Rating, above reference
          subtracts them. Each country's income level scales willingness to pay — where you sell is decided in the
          Distribution screen.
        </div>
      </div>

      <div className="grid2" style={{ marginBottom: 14 }}>
        {models.map(productCard)}
      </div>
    </div>
  );
}
